import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DraftNavigationProvider } from './core/draft-backup.js';
import { AuthProvider } from './core/auth.js';
import { App } from './App.js';
import { dismissLaunchScreen } from './core/launch-screen.js';
import { registerUpdateGuards, startAppUpdates } from './core/app-update.js';
import { getSyncState } from './offline/sync.js';
import '@fontsource-variable/figtree';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      // Seules les requêtes réellement observées par la page active sont
      // rafraîchies, jamais dans un onglet masqué.
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      // Hors ligne, le premier essai part quand même (WEB1-02) : une lecture qui
      // se replie sur l'appareil tombe sur ses données au lieu d'attendre le
      // réseau en silence ; seuls les nouveaux essais attendent son retour.
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Un geste n'est jamais mis en pause par React Query : sans réseau, son
      // envoi échoue tout de suite et l'écran le met en file avec l'heure du
      // geste, ou dit l'échec (WEB1-02). Les mutations ne réessaient pas seules.
      networkMode: 'offlineFirst',
    },
  },
});

// A new release is never applied over work in progress: a save being sent, or
// operations still waiting in the offline queue (see core/app-update.ts).
registerUpdateGuards({
  saving: () => queryClient.isMutating() > 0,
  queuePending: () => {
    const sync = getSyncState();
    return sync.pendingOps + sync.pendingMedia > 0;
  },
});
// The development server has no service worker: production builds only.
if (import.meta.env.PROD) startAppUpdates();

const root = createRoot(document.getElementById('root')!);
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <AuthProvider>
        <DraftNavigationProvider>
          <App />
        </DraftNavigationProvider>
      </AuthProvider>
    ),
  },
]);
async function renderApplication(): Promise<void> {
  try {
    // Do not mount account queries until the old shared cache is gone.
    if ('caches' in window) await window.caches.delete('api-cache');
  } catch {
    dismissLaunchScreen();
    root.render(
      <main className="container" role="alert">
        <h1>Initialisation interrompue</h1>
        <p>Le navigateur n’a pas pu nettoyer un ancien cache. Réessayez pour ouvrir MyEquip.</p>
        <button
          type="button"
          onClick={() => {
            renderApplication();
          }}
        >
          Réessayer
        </button>
      </main>,
    );
    return;
  }
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
renderApplication();
